import { useState, useEffect } from "react";
import { 
  Calendar, 
  Clock, 
  CreditCard, 
  Award, 
  CheckCircle, 
  ChevronRight,
  Sparkles,
  Zap,
  Users,
  ArrowRight
} from "lucide-react";

const PremiumFeeBatchDetails = () => {
  const [showScholarshipModal, setShowScholarshipModal] = useState(false);
  const [showEmiModal, setShowEmiModal] = useState(false);
  // const [expandedCard, setExpandedCard] = useState(null); // Kept for context if needed later

  // Typing animation for the main title
  const [typedText, setTypedText] = useState("");
  const fullText = "Invest in your future with flexible program options";
  
  useEffect(() => {
    let typingTimeout;
    if (typedText.length < fullText.length) {
      typingTimeout = setTimeout(() => {
        setTypedText(fullText.substring(0, typedText.length + 1));
      }, 80); 
    }
    return () => clearTimeout(typingTimeout);
  }, [typedText, fullText]);

  // State and effect for responsive mobile view
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const checkMobileView = () => {
      // Ensure window is defined (for server-side rendering or build environments)
      if (typeof window !== 'undefined') {
        setIsMobileView(window.innerWidth < 768); 
      }
    };

    checkMobileView();
    if (typeof window !== 'undefined') {
        window.addEventListener('resize', checkMobileView);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', checkMobileView);
      }
    };
  }, []);

  // Base styles for stat cards to avoid repetition
  const baseStatCardStyle = {
    position: 'relative',
    background: 'linear-gradient(145deg, #1a2035, #141c2f)',
    padding: '2rem',
    borderRadius: '1rem',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    cursor: 'pointer'
  };

  // Dynamic styling for hero section button flex direction
  const heroButtonContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    marginTop: '2.5rem',
    flexDirection: isMobileView ? 'column' : 'row', // Dynamic flex direction
  };


  return (
    <div style={{ 
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      backgroundColor: '#0f1419', // Dark background
      color: 'white',
      minHeight: '100vh',
      width: '100%'
    }}>
      
      {/* Navigation Bar Placeholder - Add your actual navigation bar here */}
      {/* <div style={{
        position: 'sticky', top: 0, width: '100%', background: 'rgba(26, 32, 53, 0.8)', 
        backdropFilter: 'blur(10px)', zIndex: 50, borderBottom: '1px solid rgba(255,255,255,0.1)',
        padding: '1rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div style={{fontSize: '1.5rem', fontWeight: 'bold'}}>EduFlex</div>
        <div><button style={{padding: '0.5rem 1rem', background: '#3b82f6', borderRadius: '0.5rem'}}>Login</button></div>
      </div> 
      */}

      {/* Hero Section */}
      <div style={{
        position: 'relative',
        width: '100%',
        minHeight: '70vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(ellipse at center, rgba(46, 82, 164, 0.2) 0%, rgba(13, 20, 33, 0.4) 70%, rgba(13, 20, 33, 0.7) 100%), #0f1419',
        overflow: 'hidden',
        padding: '4rem 1rem' // Responsive padding
      }}>
        {/* Animated background elements */}
        <div style={{
          position: 'absolute',
          width: isMobileView ? '250px' : '400px', // Smaller on mobile
          height: isMobileView ? '250px' : '400px',
          borderRadius: '50%',
          background: 'rgba(59, 130, 246, 0.08)', // Softer blur
          filter: 'blur(50px)', // Softer blur
          top: '-80px',
          left: '-40px',
          animation: 'pulse 5s ease-in-out infinite alternate' // Slower and alternating
        }}></div>
        
        <div style={{
          position: 'absolute',
          width: isMobileView ? '200px' : '300px',
          height: isMobileView ? '200px' : '300px',
          borderRadius: '50%',
          background: 'rgba(99, 102, 241, 0.08)',
          filter: 'blur(40px)',
          bottom: '-20px', // Adjusted position
          right: '-30px',
          animation: 'pulse 6s ease-in-out infinite alternate 1s' // Slower, alternating, delayed
        }}></div>
        
        {/* Hero Content */}
        <div style={{ textAlign: 'center', maxWidth: '800px', position: 'relative', zIndex: 10 }}>
          <div style={{
            display: 'inline-block',
            marginBottom: '1.5rem',
            padding: '0.5rem 1rem',
            background: 'rgba(255, 255, 255, 0.05)', // More subtle
            backdropFilter: 'blur(8px)',
            borderRadius: '50px',
            fontSize: '0.875rem',
            color: 'rgba(255, 255, 255, 0.85)',
            fontWeight: '500',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <span style={{ color: '#60a5fa', marginRight: '0.25rem' }}>•</span>
            Premium Tech Education
          </div>
          
          <h1 style={{ // Changed to h1 for semantic correctness
            fontSize: 'clamp(2.5rem, 7vw, 4.5rem)', // Adjusted clamp for better responsiveness
            fontWeight: 'bold',
            marginBottom: '1.5rem', // Reduced margin
            background: 'linear-gradient(135deg, white, #e0f2fe, #a5f3fc)', // Brighter gradient
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            lineHeight: 1.15 // Slightly increased line height
          }}>
            Fee & Batch Details
          </h1>
          
          <p style={{
            fontSize: 'clamp(1rem, 3.5vw, 1.375rem)', // Adjusted clamp
            color: 'rgba(255, 255, 255, 0.75)', // Slightly brighter
            marginBottom: '2.5rem', // Increased margin
            lineHeight: 1.65,
            maxWidth: '650px', // Constrain width for readability
            margin: '0 auto 2.5rem auto'
          }}>
            {typedText}
            <span style={{
              display: 'inline-block',
              width: '3px', // Slightly thicker
              height: '1.1em', // Adjusted height
              backgroundColor: '#60a5fa',
              marginLeft: '4px',
              animation: 'blink 1.2s step-end infinite', // Slower blink
              verticalAlign: 'text-bottom' // Better alignment
            }}></span>
          </p>
          
          <div style={heroButtonContainerStyle}>
            <a
              target="_default" href="https://calendly.com/logicmojo/schedule-call-back-from-experts-for-live-classes?month=2025-05"
              style={{
                display: 'inline-flex', // Changed to inline-flex for better icon alignment
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.875rem 1.75rem', // Adjusted padding
                background: 'linear-gradient(135deg, #3b82f6, #6366f1)', // Tailwind blue-500 to indigo-500
                color: 'white',
                borderRadius: '50px',
                fontWeight: '600', // Bolder
                fontSize: '1rem', // Adjusted size
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 8px 20px rgba(59, 130, 246, 0.25)', // Softer shadow
                transition: 'all 0.3s ease',
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px) scale(1.03)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0) scale(1)'}
            >
            Request Callback
              <ChevronRight size={18} style={{ marginLeft: '0.25rem' }} />
            </a>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        backgroundColor: '#0d1421', // Slightly lighter dark
        padding: isMobileView ? '0rem 1rem' : '0rem 2rem', // Responsive padding
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          
          {/* Stats Section - MODIFIED FOR HORIZONTAL SCROLLING ON MOBILE */}
          <div className={isMobileView ? 'stats-container-mobile' : ''} // Class for Webkit scrollbar hide
             style={{
                display: isMobileView ? 'flex' : 'grid',
                flexDirection: isMobileView ? 'row' : undefined,
                overflowX: isMobileView ? 'auto' : 'visible',
                gridTemplateColumns: !isMobileView ? 'repeat(auto-fit, minmax(240px, 1fr))' : undefined, // Slightly smaller min for grid
                gap: '1.5rem',
                marginBottom: '5rem',
                paddingTop: isMobileView ? '0.5rem' : '0', // Ensure cards are not cut off
                paddingBottom: isMobileView ? '1.5rem' : '0', // Space for scrollbar and visual appeal
                WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
                ...(isMobileView && {
                    msOverflowStyle: 'none',  /* IE and Edge */
                    scrollbarWidth: 'none',  /* Firefox */
                }),
            }}>
            {[
              { label: "Program Duration", value: "6 Months", icon: Calendar, color: '#3b82f6' },
              { label: "Placement Rate", value: "97%", icon: Users, color: '#10b981' },
              { label: "Industry Partners", value: "200+", icon: Zap, color: '#f59e0b' },
              { label: "Alumni Network", value: "50,000+", icon: Users, color: '#8b5cf6' }
            ].map((stat, index) => (
              <div 
                key={index} 
                style={{
                  ...baseStatCardStyle,
                  minWidth: isMobileView ? '260px' : undefined, 
                  flexShrink: isMobileView ? 0 : undefined,
                  // Add a small margin to the right of each card except the last one for mobile flex view
                  marginRight: isMobileView && index < 3 ? '1rem' : '0',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px) scale(1.01)';
                  e.currentTarget.style.boxShadow = `0 15px 30px ${stat.color}2A`; 
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}>
                <div style={{
                  position: 'absolute',
                  top: '-1rem',
                  right: '-1rem',
                  width: '6rem',
                  height: '6rem',
                  borderRadius: '50%',
                  background: `${stat.color}1A`,
                  filter: 'blur(20px)'
                }}></div>
                
                <div style={{ position: 'relative', zIndex: 10 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '1rem'
                  }}>
                    <div style={{
                      padding: '0.75rem',
                      background: '#0f1419', // Darker inner bg
                      borderRadius: '0.5rem'
                    }}>
                      <stat.icon size={24} color={stat.color} />
                    </div>
                    <div style={{
                      height: '2.5rem',
                      width: '2.5rem',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      color: 'rgba(255, 255, 255, 0.5)', // Brighter
                      fontFamily: 'monospace',
                      border: '1px solid rgba(255, 255, 255, 0.15)' // Brighter border
                    }}>
                      0{index + 1}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '1.875rem', // Slightly smaller for better fit
                    fontWeight: 'bold',
                    marginBottom: '0.25rem',
                    color: 'white'
                  }}>{stat.value}</div>
                  <div style={{
                    fontSize: '0.875rem',
                    color: 'rgba(255, 255, 255, 0.65)' // Brighter
                  }}>{stat.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Section Title */}
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h3 style={{
              fontSize: 'clamp(2rem, 5vw, 2.8rem)', // Adjusted size
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, white, #e0f2fe, #a5f3fc)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '1rem'
            }}>
              Program Details
            </h3>
            <p style={{
              color: 'rgba(255, 255, 255, 0.65)',
              maxWidth: '600px',
              margin: '0 auto',
              fontSize: '1.05rem' // Adjusted size
            }}>
              Choose from flexible batches and payment options designed to fit your schedule and budget
            </p>
          </div>

          {/* Interactive Cards Section */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', // Adjusted minmax
            gap: '2rem',
            marginBottom: '6rem'
          }}>
            
            {/* Program Fee Card */}
            <div style={{
              position: 'relative', borderRadius: '1.5rem', background: 'linear-gradient(145deg, #1e293b, #171e2b)', // Slightly different dark
              border: '1px solid rgba(255, 255, 255, 0.08)', overflow: 'hidden', transition: 'all 0.4s ease-out', cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
              e.currentTarget.style.boxShadow = '0 20px 45px rgba(59, 130, 246, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = 'none';
            }}>
              <div style={{ position: 'relative', zIndex: 10, padding: '2rem' }}>
                {/* ... (Fee Card Content - kept same as your provided snippet for brevity) ... */}
                 <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                  <div style={{ padding: '0.75rem', background: 'rgba(59, 130, 246, 0.15)', borderRadius: '0.5rem' }}><CreditCard size={24} color="#60a5fa" /></div>
                  <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(96, 165, 250, 0.8)', fontWeight: '500' }}>Premium Package</div>
                </div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', marginBottom: '1.5rem' }}>Program Fee</h3>
                <div style={{ display: 'flex', alignItems: 'end', marginBottom: '1.5rem' }}>
                  <div style={{ fontSize: '3rem', fontWeight: 'bold', color: 'white' }}>₹65,000</div>
                  <div style={{ marginLeft: '0.75rem', color: 'rgba(255, 255, 255, 0.5)', paddingBottom: '0.25rem' }}>(GST inclusive)</div>
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  {['15% Discount - First 15 People', 'Lifetime access content', 'Job placement assistance'].map((feature, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                      <div style={{ height: '4px', width: '4px', borderRadius: '50%', backgroundColor: '#60a5fa', marginRight: '0.5rem' }}></div>
                      <div style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{feature}</div>
                    </div>
                  ))}
                </div>
                <div style={{ background: '#141c2f', border: '1px solid rgba(255, 255, 255, 0.05)', borderRadius: '0.75rem', padding: '1rem', marginBottom: '2rem' }}>
                  <div style={{ textAlign: 'center', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.8)', fontWeight: '500' }}>EMI Options</div>
                  <div style={{ textAlign: 'center', marginBottom: '0.25rem' }}>
                    <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Starting at </span>
                    <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>₹4,500</span>
                    <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>/month</span>
                  </div>
                  <div style={{ fontSize: '0.75rem', textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)' }}>No Cost EMI available with select banks</div>
                </div>
                <button onClick={() => setShowEmiModal(true)} style={{ width: '100%', padding: '0.75rem 1rem', background: 'linear-gradient(135deg, #2563eb, #6366f1)', color: 'white', borderRadius: '0.5rem', fontWeight: '500', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', transition: 'all 0.3s ease' }}>View EMI Options <ArrowRight size={16} /></button>
              </div>
            </div>

            {/* Batch Details Card (Content kept same as your snippet for brevity) */}
            <div style={{
              position: 'relative', borderRadius: '1.5rem', background: 'linear-gradient(145deg, #1e293b, #171e2b)',
              border: '1px solid rgba(255, 255, 255, 0.08)', overflow: 'hidden', transition: 'all 0.4s ease-out', cursor: 'pointer'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)'; e.currentTarget.style.boxShadow = '0 20px 45px rgba(139, 92, 246, 0.15)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0) scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}>
              <div style={{ position: 'relative', zIndex: 10, padding: '2rem' }}>
                 {/* ... (Batch Card Content - kept same as your provided snippet for brevity) ... */}
                 <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                  <div style={{ padding: '0.75rem', background: 'rgba(139, 92, 246, 0.15)', borderRadius: '0.5rem' }}><Calendar size={24} color="#a78bfa" /></div>
                  <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(167, 139, 250, 0.8)', fontWeight: '500' }}>Flexible Schedule</div>
                </div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', marginBottom: '1.5rem' }}>Batch Details</h3>
                <div style={{ marginBottom: '2rem' }}> {/* Weekend Batch */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <div style={{ background: 'rgba(249, 115, 22, 0.15)', color: '#fb923c', fontWeight: '500', padding: '0.25rem 0.75rem', borderRadius: '50px', fontSize: '0.875rem' }}>Weekend Batch</div>
                    <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Clock size={16} color="rgba(251, 146, 60, 0.7)" /> 9:00 AM - 12 PM</div>
                  </div>
                  <div style={{ background: '#141c2f', border: '1px solid rgba(255, 255, 255, 0.05)', borderRadius: '0.75rem', padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ height: '3rem', width: '3rem', borderRadius: '0.5rem', background: 'rgba(249, 115, 22, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fb923c', fontWeight: 'bold' }}>23</div>
                      <div style={{ marginLeft: '0.75rem' }}><div style={{ color: 'white', fontWeight: '500' }}>Aug 2025</div><div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.875rem' }}>SAT - SUN</div></div>
                    </div>
                    <div style={{ background: '#1a2035', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '0.5rem', padding: '0.5rem' }}><Calendar size={20} color="rgba(251, 146, 60, 0.7)" /> 7 months</div>
                  </div>
                </div>
                <div> {/* Weekday Batch */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <div style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', fontWeight: '500', padding: '0.25rem 0.75rem', borderRadius: '50px', fontSize: '0.875rem' }}>Weekday Batch</div>
                    <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Clock size={16} color="rgba(96, 165, 250, 0.7)" /> 8:00 PM - 10:00 PM</div>
                  </div>
                  <div style={{ background: '#141c2f', border: '1px solid rgba(255, 255, 255, 0.05)', borderRadius: '0.75rem', padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ height: '3rem', width: '3rem', borderRadius: '0.5rem', background: 'rgba(59, 130, 246, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#60a5fa', fontWeight: 'bold' }}>25</div>
                      <div style={{ marginLeft: '0.75rem' }}><div style={{ color: 'white', fontWeight: '500' }}>Aug 2025</div><div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.875rem' }}>MON - THU</div></div>
                    </div>
                    <div style={{ background: '#1a2035', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '0.5rem', padding: '0.5rem' }}><Calendar size={20} color="rgba(96, 165, 250, 0.7)" /> 5 months</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Certification & Career Support Card (Content kept same as your snippet for brevity) */}
            <div style={{
              position: 'relative', borderRadius: '1.5rem', background: 'linear-gradient(145deg, #1e293b, #171e2b)',
              border: '1px solid rgba(255, 255, 255, 0.08)', overflow: 'hidden', transition: 'all 0.4s ease-out', cursor: 'pointer'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)'; e.currentTarget.style.boxShadow = '0 20px 45px rgba(6, 182, 212, 0.15)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0) scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}>
              <div style={{ position: 'relative', zIndex: 10, padding: '2rem' }}>
                {/* ... (Certification Card Content - kept same as your provided snippet for brevity) ... */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                  <div style={{ padding: '0.75rem', background: 'rgba(6, 182, 212, 0.15)', borderRadius: '0.5rem' }}><Award size={24} color="#22d3ee" /></div>
                  <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(34, 211, 238, 0.8)', fontWeight: '500' }}>Career Excellence</div>
                </div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>🎓 Certification & Career Support</h3>
                <div style={{ marginBottom: '1.5rem' }}> {/* Professional Certificate */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <div style={{ height: '8px', width: '8px', borderRadius: '50%', backgroundColor: '#22d3ee', marginRight: '0.75rem', marginTop: '0.5rem', flexShrink: 0 }}></div>
                    <div><div style={{ fontSize: '1.1rem', fontWeight: '600', color: 'white', marginBottom: '0.25rem' }}>Professional Certificate</div><div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem', lineHeight: '1.4' }}>Earn a verified, industry-recognized credential upon completion.</div></div>
                  </div>
                </div>
                <div style={{ marginBottom: '1.5rem' }}> {/* Mock Interviews */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <div style={{ height: '8px', width: '8px', borderRadius: '50%', backgroundColor: '#22d3ee', marginRight: '0.75rem', marginTop: '0.5rem', flexShrink: 0 }}></div>
                    <div><div style={{ fontSize: '1.1rem', fontWeight: '600', color: 'white', marginBottom: '0.25rem' }}>Mock Interviews & Resume Review</div><div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem', lineHeight: '1.4' }}>1:1 coaching to sharpen your interview skills and polish your CV.</div></div>
                  </div>
                </div>
                <div style={{ marginBottom: '1.5rem' }}> {/* Job Referrals */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <div style={{ height: '8px', width: '8px', borderRadius: '50%', backgroundColor: '#22d3ee', marginRight: '0.75rem', marginTop: '0.5rem', flexShrink: 0 }}></div>
                    <div><div style={{ fontSize: '1.1rem', fontWeight: '600', color: 'white', marginBottom: '0.25rem' }}>Job Referrals & Networking</div><div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem', lineHeight: '1.4' }}>Get Job referral in Data Scientist, ML engineers and AI engineers Roles.</div></div>
                  </div>
                </div>
                <div style={{ marginBottom: '2rem' }}> {/* Lifetime Career Resources */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <div style={{ height: '8px', width: '8px', borderRadius: '50%', backgroundColor: '#22d3ee', marginRight: '0.75rem', marginTop: '0.5rem', flexShrink: 0 }}></div>
                    <div><div style={{ fontSize: '1.1rem', fontWeight: '600', color: 'white', marginBottom: '0.25rem' }}>Lifetime Career Resources</div><div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem', lineHeight: '1.4' }}>On-demand webinars, GitHub portfolio reviews, and hiring-playbook guides.</div></div>
                  </div>
                </div>
                <div style={{ background: '#141c2f', border: '1px solid rgba(255, 255, 255, 0.05)', borderRadius: '0.75rem', padding: '1rem', marginBottom: '2rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}><Sparkles size={16} color="#fbbf24" style={{ marginRight: '0.5rem' }} /><div style={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: '500' }}>Career Excellence Program</div></div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem' }}>Comprehensive support system designed to accelerate your professional growth and job placement success.</div>
                </div>
                <a target="_default" href="https://calendly.com/logicmojo/schedule-call-back-from-experts-for-live-classes?month=2025-05" style={{ width: '100%', padding: '0.75rem 1rem', background: 'linear-gradient(135deg, #0891b2, #2563eb)', color: 'white', borderRadius: '0.5rem', fontWeight: '500', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', transition: 'all 0.3s ease', textDecoration: 'none' }}>Learn More <ArrowRight size={16} /></a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals (Scholarship & EMI) - Simplified for brevity, no structural changes from your snippet */}
      {showScholarshipModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem' }}>
          {/* ... Scholarship Modal Content (same as your last full code snippet) ... */}
        </div>
      )}
      {showEmiModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem' }}>
          {/* ... EMI Modal Content (same as your last full code snippet) ... */}
          {showEmiModal && (
  <div style={{
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    padding: '1rem'
  }}>
    <div style={{
      position: 'relative',
      background: 'linear-gradient(145deg, #1e293b, #171e2b)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '1rem',
      maxWidth: '500px',
      width: '100%',
      padding: '2rem',
      color: 'white',
      boxShadow: '0 20px 45px rgba(0,0,0,0.4)'
    }}>
      
      {/* Close Button */}
      <button 
        onClick={() => setShowEmiModal(false)}
        style={{
          position: 'absolute',
          top: '1rem',
          right: '1rem',
          background: 'rgba(255,255,255,0.1)',
          border: 'none',
          borderRadius: '50%',
          width: '32px',
          height: '32px',
          color: 'white',
          fontSize: '1.5rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: '1',
          transition: 'background 0.2s ease'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
      >
        &times;
      </button>

      {/* Modal Header */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>Flexible EMI Options</h2>
        <p style={{ color: 'rgba(255,255,255,0.6)' }}>Total Fee: ₹65,000 (GST Inclusive)</p>
      </div>

      {/* EMI Plans */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {[
          { months: 3, cost: "21,667", type: "No Cost EMI" },
          { months: 6, cost: "10,833", type: "No Cost EMI" },
          { months: 9, cost: "7,222", type: "No Cost EMI" },
          { months: 12, cost: "5,800", type: "Low Cost EMI" },
        ].map((plan, index) => (
          <div key={index} style={{
            background: '#141c2f',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '0.75rem',
            padding: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>{plan.months} Months Plan</div>
              <div style={{ fontSize: '0.875rem', color: plan.type === 'No Cost EMI' ? '#10b981' : '#f59e0b' }}>{plan.type}</div>
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>₹{plan.cost}/mo</div>
          </div>
        ))}
      </div>

      {/* Call to Action */}
      <a 
        href="https://logicmojo.com/course/Live-Classes-for-SDE-Sheet" 
        target="_blank" 
        rel="noopener noreferrer"
        style={{
          display: 'block',
          width: '100%',
          marginTop: '2rem',
          padding: '0.875rem 1rem',
          textAlign: 'center',
          background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
          color: 'white',
          borderRadius: '0.5rem',
          fontWeight: '600',
          textDecoration: 'none',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease'
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.25)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
      >
        Select Plan & Enroll
      </a>
    </div>
  </div>
)}
        </div>
      )}
      
      <style jsx global>{`
        /* Global styles can be added here if needed, but prefer component-scoped styles */
        body {
          margin: 0; /* Ensure no default body margin */
        }
      `}</style>
      <style>{`
        @keyframes pulse { 
          0%, 100% { opacity: 0.2; transform: scale(0.9); } 
          50% { opacity: 0.4; transform: scale(1); } 
        }
        @keyframes blink { 
          0%, 100% { opacity: 0; } 
          50% { opacity: 1; } 
        }
        * { box-sizing: border-box; }

        /* Styles for hiding scrollbar on mobile for the stats container */
        .stats-container-mobile::-webkit-scrollbar {
          display: none; /* Safari and Chrome */
        }
        .stats-container-mobile {
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;  /* Firefox */
        }
      `}</style>
    </div>
  );
};

export default PremiumFeeBatchDetails;

